import { inject, Injector, runInInjectionContext } from '@angular/core';
import { OperatorType } from 'angular-slickgrid';
import { map, of } from 'rxjs';
import { LocationService, StandService } from 'cbd-proxies';
import { CycleProjectionPlanningOrchestratorService, RotationProjectionPlanningOrchestratorService } from 'orchestrator';
import {
  ControlTypes,
  FieldBuilder,
  FieldDefinition,
  ForeignKeyConfig,
  LocationTypes,
  MenuCategories,
  MenuSubcategories,
  MENU_CATEGORY_KEY,
  MENU_NAME_SUFFIX,
  MENU_SUBCATEGORY_KEY,
  ZnSelectFilter,
  ZnSelectQueryParams,
} from '@zendra/shared';
import { CalculationResultPlanningOrchestratorService } from './calculation-result-orchestrator.service';

FieldBuilder.setNamespace('Inventory');

const loadCalculationResultRegions = (service: LocationService, query: any, config?: any) =>
  service.getList(
    {
      ...query,
      locationTypeEntityName: LocationTypes.Region,
      isActive: true,
    },
    config,
  );

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value > 0;

const parseLocationId = (value: string): number | null => {
  const parsedValue = Number(value);

  return isPositiveInteger(parsedValue) ? parsedValue : null;
};

const extractLocationIds = (rawValue: unknown): number[] => {
  if (typeof rawValue === 'number') {
    return isPositiveInteger(rawValue) ? [rawValue] : [];
  }

  if (typeof rawValue !== 'string') {
    return [];
  }

  return rawValue
    .split(',')
    .map(part => parseLocationId(part.trim()))
    .filter((parsedValue): parsedValue is number => parsedValue !== null);
};

const normalizeLocationIds = (value: unknown): number[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  const rawValues = Array.isArray(value) ? value : [value];

  return [...new Set(rawValues.flatMap(extractLocationIds))];
};

const loadCalculationResultProperties = (query?: any, config?: any) => {
  const service = inject(CalculationResultPlanningOrchestratorService);
  return service
    .getProperties(
      {
        regionLocationIds: normalizeLocationIds(query?.regionLocationIds),
        name: query?.name ?? query?.search,
        skipCount: query?.skipCount ?? 0,
        maxResultCount: query?.maxResultCount ?? 20,
      },
      config,
    )
    .pipe(map(result => result.items));
};

const loadCalculationResultPropertyOptions = (query?: any) =>
  loadCalculationResultProperties(query, { skipHandleError: true }).pipe(
    map(items => items.map(item => ({ key: String(item.locationId), value: item.name }))),
  );

const createCalculationResultPropertyPrefilterField = (): FieldDefinition => {
  const field = FieldBuilder
    .number('propertyLocationId', 'Entity.CalculationResult.PropertyLocationId')
    .build();

  const foreignKey: ForeignKeyConfig = {
    loader: (_forceRefresh?: boolean, queryOverride?: any, config?: any) =>
      loadCalculationResultProperties(queryOverride, config),
    displayField: 'name',
    keyField: 'locationId',
  };

  field.foreignKey = foreignKey;
  field.fieldOptions.controlType = ControlTypes.Dropdown;
  field.fieldOptions.type = 'foreignKey';
  field.fieldOptions.optionsLoader = params => loadCalculationResultPropertyOptions(params);
  field.fieldOptions.prefilter = {
    multiple: true,
    order: 2,
    dependsOn: { regionLocationId: 'regionLocationIds' },
  };

  return field;
};

const loadCalculationResultStandOptions = (params?: ZnSelectQueryParams) => {
  if (params?.ids?.length && !params.search) {
    return of(params.ids.map(name => ({ key: name, value: name })));
  }

  return inject(StandService).getList({
    name: params?.search?.trim() || undefined,
    locationIsActive: null,
    sorting: 'name',
    skipCount: params?.skipCount ?? 0,
    maxResultCount: params?.maxResultCount ?? 20,
  }, { skipHandleError: true }).pipe(
    map(result => ({
      items: result.items
        .filter(stand => !!stand.name?.trim())
        .map(stand => ({ key: stand.name!, value: stand.name! })),
      totalCount: result.totalCount,
    })),
  );
};

const standNameField = FieldBuilder
  .text('standName', 'Entity.Stand.Name')
  .build();

standNameField.column.createFilter = (_localizationService: unknown, injector: Injector) => ({
  model: ZnSelectFilter,
  operator: OperatorType.in,
  params: {
    injector,
    optionsLoader: (params?: ZnSelectQueryParams) =>
      runInInjectionContext(injector, () => loadCalculationResultStandOptions(params)),
    errorEntityKey: 'Entity.Stand.Name',
  },
});

export const CALCULATIONRESULT_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .foreignKey('rotationId', 'Entity.Rotation', RotationProjectionPlanningOrchestratorService)
    .display('rotationDisplay')
    .displayFromNested('standRotation')
    .sortBy('standRotation')
    .build(),
  FieldBuilder
    .foreignKey('cycleId', 'Entity.Cycle', CycleProjectionPlanningOrchestratorService)
    .display('cycleDisplay')
    .displayFromNested('standCycle')
    .sortBy('standCycle')
    .build(),
  standNameField,
  FieldBuilder
    .text('rotationUse', 'Entity.RotationUse')
    .build(),
  FieldBuilder
    .number('area', 'Entity.CalculationResult.Area')
    .decimal()
    .build(),
  FieldBuilder
    .date('inventoryDate', 'Entity.CalculationResult.InventoryDate')
    .build(),
  FieldBuilder
    .date('plantationDate', 'Entity.CalculationResult.PlantationDate')
    .build(),
  FieldBuilder
    .date('harvestDate', 'Entity.CalculationResult.HarvestDate')
    .build(),
  FieldBuilder
    .date('measurementDate', 'Entity.CalculationResult.MeasurementDate')
    .build(),
  FieldBuilder
    .number('treeAge', 'Entity.CalculationResult.TreeAge')
    .decimal()
    .build(),
  FieldBuilder
    .number('averageHeight', 'Entity.CalculationResult.AverageHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('dominantHeight', 'Entity.CalculationResult.DominantHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('treeCount', 'Entity.CalculationResult.TreeCount')
    .build(),
  FieldBuilder
    .number('basalArea', 'Entity.CalculationResult.BasalArea')
    .decimal()
    .build(),
  FieldBuilder
    .number('minimumVolume', 'Entity.CalculationResult.MinimumVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('averageVolume', 'Entity.CalculationResult.AverageVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('maximumVolume', 'Entity.CalculationResult.MaximumVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('mediumAnnualIncrement', 'Entity.CalculationResult.MediumAnnualIncrement')
    .decimal()
    .build(),
  FieldBuilder
    .number('treeVolume', 'Entity.CalculationResult.TreeVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('error', 'Entity.CalculationResult.Error')
    .decimal()
    .build(),
  FieldBuilder
    .number('plotCount', 'Entity.CalculationResult.PlotCount')
    .build(),
  FieldBuilder
    .number('sampleIntensity', 'Entity.CalculationResult.SampleIntensity')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedVolumePerHectare', 'Entity.CalculationResult.ProjectedVolumePerHectare')
    .decimal()
    .build(),
  FieldBuilder
    .number('volumePerHectare', 'Entity.CalculationResult.VolumePerHectare')
    .decimal()
    .build(),
  FieldBuilder
    .checkbox('shouldPick', 'Entity.CalculationResult.ShouldPick')
    .build(),
  FieldBuilder
    .checkbox('isLastInventory', 'Entity.CalculationResult.IsLastInventory')
    .build(),
  FieldBuilder
    .number('projectedTreeAge', 'Entity.CalculationResult.ProjectedTreeAge')
    .decimal()
    .build(),
  FieldBuilder
    .number('totalVolume', 'Entity.CalculationResult.TotalVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedTotalVolume', 'Entity.CalculationResult.ProjectedTotalVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedBasalArea', 'Entity.CalculationResult.ProjectedBasalArea')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedDominantHeight', 'Entity.CalculationResult.ProjectedDominantHeight')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedTreeCount', 'Entity.CalculationResult.ProjectedTreeCount')
    .build(),
  FieldBuilder
    .number('projectedMediumAnnualIncrement', 'Entity.CalculationResult.ProjectedMediumAnnualIncrement')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedAverageVolume', 'Entity.CalculationResult.ProjectedAverageVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('projectedTreeVolume', 'Entity.CalculationResult.ProjectedTreeVolume')
    .decimal()
    .build(),
  FieldBuilder
    .number('minimumBetweenLine', 'Entity.CalculationResult.MinimumBetweenLine')
    .decimal()
    .build(),
  FieldBuilder
    .number('mediumBetweenLine', 'Entity.CalculationResult.MediumBetweenLine')
    .decimal()
    .build(),
  FieldBuilder
    .number('maximumBetweenLine', 'Entity.CalculationResult.MaximumBetweenLine')
    .decimal()
    .build(),
  FieldBuilder
    .checkbox('cropCodeShouldPick', 'Entity.CalculationResult.CropCodeShouldPick')
    .build(),
  createCalculationResultPropertyPrefilterField(),
  FieldBuilder
    .text('propertyLocationName', 'Entity.Property')
    .build(),
  FieldBuilder
    .foreignKey('regionLocationId', 'Entity.CalculationResult.RegionLocationId', LocationService)
    .loadWith((service, query, config) => loadCalculationResultRegions(service, query, config))
    .display('name')
    .keyField('id')
    .displayFromNested('regionLocationName')
    .sortBy('regionLocationName')
    .prefilter({ multiple: true, order: 1 })
    .build(),
  FieldBuilder
    .text('regionLocationName', 'Entity.Region')
    .build(),
  FieldBuilder
    .text('team', 'Entity.Team')
    .build(),
  FieldBuilder
    .text('inventoryType', 'Entity.InventoryType')
    .build(),
  FieldBuilder
    .text('inventorySubType', 'Entity.InventorySubType')
    .build(),
  FieldBuilder
    .text('geneticMaterial', 'Entity.GeneticMaterial')
    .build(),
  FieldBuilder
    .text('geneticMaterialType', 'Entity.GeneticMaterialType')
    .build(),
  FieldBuilder
    .text('species', 'Entity.Species')
    .build(),
  FieldBuilder
    .checkbox('isActive', 'Entity.Location.IsActive')
    .build(),
  FieldBuilder
    .checkbox('isOwnPlantation', 'Entity.Plantation.IsOwnPlantation')
    .build(),
];

export const CALCULATIONRESULT_CONFIG = {
  entityName: 'CalculationResult',
  localizationKey: 'Inventory::Entity.CalculationResult',
  fields: CALCULATIONRESULT_FIELDS,
  permission: 'PlanningInventory.CalculationResults',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Inventory + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.QueriesInventory + MENU_NAME_SUFFIX,
};
